import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import type { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import {
  useVerifyGstMutation,
  useVerifyPanMutation,
} from '@/features/auth/api/useAuthMutations';
import type {
  FormValues,
  GstVerifyResponse,
  KycDetails,
  OnboardingFieldRow,
  PanVerifyResponse,
} from '@/features/auth/types/onboardingFields';
import { KYC } from '@/features/auth/types/onboardingFields';
import { fieldKey } from '@/features/auth/utils/onboardingFields';
import {
  buildGstRows,
  buildPanRows,
  getPendingKycFieldLabels,
  getPendingVerifyMessage,
  isFieldLocked,
  isKycFieldName,
  isKycFieldVerified,
  shouldShowVerifyButton,
} from '@/features/auth/utils/kycFieldLogic';

type KycStatus = { panVerified?: boolean; gstVerified?: boolean };

type UseUserDetailKycParams = {
  watch: UseFormWatch<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  kycStatus?: KycStatus;
  savedDetails: FormValues;
  savedVerified: Record<string, string>;
  savedKycDetails: {
    panDetails?: KycDetails | null;
    gstDetails?: KycDetails | null;
  };
  verifiedStorageKey: string;
  detailsStorageKey: string;
  companyFieldName?: string;
  onboardingFields: OnboardingFieldRow[];
};

export function useUserDetailKyc({
  watch,
  setValue,
  kycStatus,
  savedDetails,
  savedVerified,
  savedKycDetails,
  verifiedStorageKey,
  detailsStorageKey,
  companyFieldName,
  onboardingFields,
}: UseUserDetailKycParams) {
  const verifyPan = useVerifyPanMutation();
  const verifyGst = useVerifyGstMutation();

  const [verifiedValues, setVerifiedValues] =
    useState<Record<string, string>>(savedVerified);
  const [editingFields, setEditingFields] = useState<Record<string, boolean>>(
    {},
  );
  const [panDetails, setPanDetails] = useState<KycDetails | null>(
    savedKycDetails.panDetails || null,
  );
  const [gstDetails, setGstDetails] = useState<KycDetails | null>(
    savedKycDetails.gstDetails || null,
  );

  const persistKycDetails = useCallback(
    (next: {
      panDetails: KycDetails | null;
      gstDetails: KycDetails | null;
    }) => {
      sessionStorage.setItem(detailsStorageKey, JSON.stringify(next));
    },
    [detailsStorageKey],
  );

  const setCompanyFromGst = useCallback(
    (legalName?: string) => {
      if (!legalName || !companyFieldName) return;
      setValue(companyFieldName, legalName, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [companyFieldName, setValue],
  );

  useEffect(() => {
    if (gstDetails?.legal_name_of_business) {
      setCompanyFromGst(gstDetails.legal_name_of_business);
    }
  }, [gstDetails?.legal_name_of_business, setCompanyFromGst]);

  const markFieldVerified = useCallback(
    (name: string, value: string) => {
      setVerifiedValues((prev) => {
        const next = { ...prev, [name]: value };
        sessionStorage.setItem(verifiedStorageKey, JSON.stringify(next));
        return next;
      });
    },
    [verifiedStorageKey],
  );

  const clearEditingField = useCallback((name: string) => {
    setEditingFields((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const checkShouldShowVerifyButton = useCallback(
    (name: string) =>
      shouldShowVerifyButton({
        name,
        currentValue: watch(name),
        editingFields,
        verifiedValues,
        kycStatus,
        savedDetails,
      }),
    [editingFields, kycStatus, savedDetails, verifiedValues, watch],
  );

  const isGstEditing = useMemo(
    () =>
      Object.keys(editingFields).some(
        (k) => fieldKey(k) === KYC.GST && editingFields[k],
      ),
    [editingFields],
  );

  const checkIsFieldLocked = useCallback(
    (name: string) =>
      isFieldLocked({
        name,
        currentValue: watch(name),
        gstDetails,
        isGstEditing,
        shouldShowVerify: checkShouldShowVerifyButton(name),
      }),
    [checkShouldShowVerifyButton, gstDetails, isGstEditing, watch],
  );

  const unlockVerifiedField = useCallback(
    (name: string) => {
      const key = fieldKey(name);
      setEditingFields((prev) => ({ ...prev, [name]: true }));
      setVerifiedValues((prev) => {
        const next = { ...prev };
        delete next[name];
        sessionStorage.setItem(verifiedStorageKey, JSON.stringify(next));
        return next;
      });
      if (key === KYC.PAN) {
        setPanDetails(null);
        persistKycDetails({ panDetails: null, gstDetails });
      } else if (key === KYC.GST) {
        setGstDetails(null);
        persistKycDetails({ panDetails, gstDetails: null });
      }
    },
    [gstDetails, panDetails, persistKycDetails, verifiedStorageKey],
  );

  const watchedValues = watch();

  const checkIsKycVerified = useCallback(
    (name: string) =>
      isKycFieldVerified({
        name,
        currentValue: watchedValues[name],
        editingFields,
        verifiedValues,
        kycStatus,
        savedDetails,
      }),
    [editingFields, kycStatus, savedDetails, verifiedValues, watchedValues],
  );

  const pendingKycFields = useMemo(
    () =>
      getPendingKycFieldLabels(
        onboardingFields,
        watchedValues,
        checkIsKycVerified,
      ),
    [checkIsKycVerified, onboardingFields, watchedValues],
  );

  const pendingVerification = pendingKycFields.length > 0;
  const pendingVerifyMessage = useMemo(
    () => getPendingVerifyMessage(pendingKycFields),
    [pendingKycFields],
  );

  const verifyPanNumber = async (name: string) => {
    try {
      const panValue = String(watch(name) || '');
      const res = (await verifyPan.mutateAsync({
        pan_number: panValue,
        pan_name: 'Advertiser',
        sessionId: localStorage.getItem('SESSION_ID') || '',
      })) as PanVerifyResponse;
      const details = res?.data?.panDetails || null;
      setPanDetails(details);
      persistKycDetails({ panDetails: details, gstDetails });
      markFieldVerified(name, panValue);
      clearEditingField(name);
      toast.success(res?.message || 'PAN verified');
      window.setTimeout(() => {
        document
          .querySelector('[data-kyc-card="pan"]')
          ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    } catch {
      /* toasted in mutation */
    }
  };

  const verifyGstNumber = async (name: string) => {
    try {
      const gstValue = String(watch(name) || '');
      const res = (await verifyGst.mutateAsync({
        gst_number: gstValue,
        sessionId: localStorage.getItem('SESSION_ID') || '',
      })) as GstVerifyResponse;
      const details =
        res?.data?.gstDetails || res?.data?.gst_details || null;
      setGstDetails(details);
      persistKycDetails({ panDetails, gstDetails: details });
      markFieldVerified(name, gstValue);
      clearEditingField(name);
      setCompanyFromGst(details?.legal_name_of_business);
      toast.success(res?.message || 'GST verified');
      window.setTimeout(() => {
        document
          .querySelector('[data-kyc-card="gst"]')
          ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    } catch {
      /* toasted in mutation */
    }
  };

  const panRows = useMemo(() => buildPanRows(panDetails), [panDetails]);
  const gstRows = useMemo(() => buildGstRows(gstDetails), [gstDetails]);

  const setVerifiedValuesFromSync = useCallback(
    (updater: (prev: Record<string, string>) => Record<string, string>) => {
      setVerifiedValues(updater);
    },
    [],
  );

  return {
    pendingVerification,
    pendingVerifyMessage,
    shouldShowVerifyButton: checkShouldShowVerifyButton,
    isKycField: isKycFieldName,
    isFieldLocked: checkIsFieldLocked,
    unlockVerifiedField,
    verifyPanNumber,
    verifyGstNumber,
    panRows,
    gstRows,
    verifyPanPending: verifyPan.isPending,
    verifyGstPending: verifyGst.isPending,
    setVerifiedValuesFromSync,
    isKycVerified: checkIsKycVerified,
  };
}
